import BigCalendarTimeZoned from "#/commons/components/BigCalendarTimeZoned";
import "#/commons/data/section/year-calendar.css";
import StationClient from "#/lib/StationClient";
import moment from 'moment';
import React from 'react';
import { FaCalculator, FaFileCsv, FaFilePdf } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";

import DailyStatsClient from '#/lib/DailyStatsClient';
import DateUtils from "#/lib/DateUtils";
import _ from 'lodash';

import Button from '@material-ui/core/Button';
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import SendSharpIcon from '@material-ui/icons/SendSharp';

import { MEASUREMENT_CATEGORY_ICONS, MEASUREMENT_LABELS } from "#/lib/MeasurementCategory";
import DailyStatsTables from "./DailyStatsTables";

import { outflowValidationOptions, rainValidationOptions, temperatureValidationOptions } from "#/lib/DailyStatsValidation";

import BaseStatsPage, { defaultYear, defaultSelectedRage } from "../components/BaseStatsPage";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const ReactSwal = withReactContent(Swal);
const loadingSwal = Swal.mixin({
	allowOutsideClick: false,
	allowEscapeKey: false,
	didOpen: () => {
		Swal.showLoading();
	},
});

class DailyStatsPage extends BaseStatsPage {

	constructor(props) {
		super(props);
		this.state = {
			...this.state,
			activeSensor: true,
		};
	}

	renderCalendarPicker() {
		return (
			<BigCalendarTimeZoned
				timeZone={"Etc/GMT-1"}
				range={this.state.interval}
				year={this.state.year}
				onPrevYear={() => this.onPrevYear()}
				onNextYear={() => this.onNextYear()}
				onPickRangeCallback={(start, end, year) => this.onPickRange(start, end, year)}
				future={day => this.isDisabled(day)}
			/>
		);
	}

	createCSVFileName = () => {
		const { selectedStation, allStations, interval: { start, end }, selectedCategory } = this.state;
		return `${MEASUREMENT_LABELS[selectedCategory]}
			${allStations ? 'ALL' : selectedStation.label}
			${moment(new Date(start)).format("DDMMYYYY")}-${moment(new Date(end)).format("DDMMYYYY")}`;
	}

	// ─── DailyStats-specific methods ─────────────────────────────────────────────

	onPrevYear() {
		this.setState({ year: this.state.year - 1 });
	}

	onNextYear() {
		this.setState({ year: this.state.year + 1 });
	}

	onPickRange(start, end, year) {
		let isTillToday = end.toDate().toLocaleDateString() === (new Date()).toLocaleDateString();
		let isFromToday = start.toDate().toLocaleDateString() === (new Date()).toLocaleDateString();
		end = isTillToday ? moment().add(-30, 'm') : end.add(1, 'd').startOf('day');
		start = isFromToday ? end.clone().add(-24, 'h') : start.startOf('day');

		this.setState({
			data: null,
			year: !!year ? year : this.state.year,
			selectedRange: [start, end],
			interval: { start, end },
			showGisPicker: false
		}, () => {
			this.fetchDailyData();
		});
	}

	onMobilePickRange(dates) {
		let interval = null;
		if (dates && dates[0] && dates[1]) {
			interval = {
				start: moment(dates[0].getTime()).startOf('day'),
				end: moment(dates[1].getTime()).endOf('day')
			};
		}
		this.resetInterval(interval);
	}

	resetInterval(interval) {
		this.setState({
			data: null,
			interval,
			selectedRange: !!interval ? [interval.start, interval.end] : defaultSelectedRage,
			year: defaultYear,
			showGisPicker: !!interval && !!this.state.selectedStation.value ? false : true
		}, () => {
			this.fetchDailyData();
		});
	}

	fetchDailyData() {
		const { selectedStation, allStations, interval, selectedCategory, stationMapByCode } = this.state;

		const onSuccess = (data) => {
			loadingSwal.close();
			this.addCalculatedFieldsToData(data, stationMapByCode);
			this.setState({ data, showGisPicker: false, tableKey: (this.state.tableKey + 1) % 1000 });
		};
		const onError = () => {
			loadingSwal.close();
			ReactSwal.fire('Errore nel caricamento dati!', '', 'error');
		};

		if (!!selectedStation && !!interval && !!selectedCategory) {
			loadingSwal.fire('Caricamento dati...');
			DailyStatsClient.getDailyStats(onSuccess, onError, {
				start: interval.start.valueOf(),
				end: interval.end.valueOf(),
				category: selectedCategory,
				stationCode: selectedStation.value
			});
		} else if (!!allStations && !!interval && !!selectedCategory) {
			loadingSwal.fire('Caricamento dati...');
			DailyStatsClient.getDailyStats(onSuccess, onError, {
				start: interval.start.valueOf(),
				end: interval.end.valueOf(),
				category: selectedCategory
			});
		} else {
			this.setState({ data: null });
		}
	}

	updateDailyStats = () => {
		let dirtyArray = this.getDirtyStats();
		loadingSwal.fire('Salvataggio dati...');
		DailyStatsClient.updateAllDailyStats(
			(data) => {
				loadingSwal.close();
				ReactSwal.fire('Salvataggio effettuato con successo', '', 'success').then(() => {
					this.fetchDailyData();
				});
			},
			(err) => {
				loadingSwal.close();
				ReactSwal.fire('Errore nel caricamento dati!', '', 'error');
			},
			this.state.selectedCategory,
			dirtyArray
		);
	}

	getDirtyStats = () => {
		let dirtyArray = [];
		this.state.data.forEach(row => {
			row.dailyStats.forEach(innerRow => {
				if (!!innerRow.dirty) {
					dirtyArray.push(innerRow);
				}
			});
		});
		return dirtyArray;
	}

	downloadDataPdf = () => {
		const { interval } = this.state;
		this.tableRef.table.download("pdf", "data.pdf", {
			orientation: "portrait",
			title: `Report statistiche giornaliere\nDal ${DateUtils.epochToDateMinuteResolution(interval.start)} al ${DateUtils.epochToDateMinuteResolution(interval.end)}`,
			autoTable: doc => {
				doc.autoTableSetDefaults({
					addPageContent: (data) => {
						let footerStr = String(doc.internal.getNumberOfPages());
						doc.setFontSize(10);
						doc.text(footerStr, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10);
						doc.setFontSize(14);
					}
				});
				return {
					styles: { fillColor: [214, 236, 237], minCellWidth: 20 },
					headStyles: { textColor: [0, 0, 0] },
					columnStyles: {
						0: { cellWidth: 60 },
						[this.tableRef.table.columnManager.columns.findIndex(col => col.field === 'validate')]: { cellWidth: 170 }
					},
					margin: { top: 70, bottom: 70 }
				};
			}
		});
	};

	addCalculatedFieldsToData = (data, stationMapByCode) => {
		data.forEach(row => {
			row.date = moment(new Date(row.timestampKey)).format("DD/MM/YYYY");
			row.dailyStats.forEach(innerRow => {
				innerRow.date = moment(new Date(innerRow.timestampKey)).format("DD/MM/YYYY");
				innerRow.stationName = stationMapByCode[innerRow.stationCode];
			});
		});
	}

	massiveValidation = (val) => {
		let data = this.state.data;
		data.forEach(row => {
			row.dirty = true;
			row.dailyStats.forEach(innerRow => {
				if (!!!innerRow.historical) {
					innerRow.dirty = true;
					innerRow.validate = val;
				}
			});
		});
		this.setState({ data, tableKey: (this.state.tableKey + 1) % 1000 });
	}

	calculateDailyStats = () => {
		let params = {
			start: this.state.interval.start.valueOf(),
			end: this.state.interval.end.valueOf(),
			category: this.state.selectedCategory
		};
		if (!this.state.allStations) {
			params = { ...params, stationCode: this.state.selectedStation.value };
		}

		loadingSwal.fire('Ricalcolo dati in corso...');
		DailyStatsClient.calculateDailyStatsByInterval(
			(data) => {
				loadingSwal.close();
				data.forEach(row => {
					row.dirty = true;
					row.dailyStats.forEach(innerRow => {
						if (innerRow.validate !== "MISSING") innerRow.dirty = true;
					});
				});
				this.addCalculatedFieldsToData(data, this.state.stationMapByCode);
				this.setState({ data, tableKey: (this.state.tableKey + 1) % 1000 });
			},
			(err) => { loadingSwal.close(); console.log("error"); },
			params
		);
	}

	calculateByDay = (elem) => {
		let params = {
			day: elem.dailyStats[0].day,
			month: elem.dailyStats[0].month,
			year: elem.dailyStats[0].year,
			category: this.state.selectedCategory
		};
		loadingSwal.fire('Ricalcolo dati in corso...');
		DailyStatsClient.calculateDailyStatsByDate(
			(data) => {
				loadingSwal.close();
				let newData = this.state.data;
				data.forEach(row => {
					row.dirty = true;
					row.dailyStats.forEach(innerRow => {
						if (innerRow.validate !== "MISSING") innerRow.dirty = true;
					});
					var index = _.findIndex(newData, { timestampKey: row.timestampKey });
					newData.splice(index, 1, row);
				});
				this.addCalculatedFieldsToData(newData, this.state.stationMapByCode);
				this.setState({ data: newData, tableKey: (this.state.tableKey + 1) % 1000 });
			},
			(err) => { loadingSwal.close(); console.log("error"); },
			params
		);
	}

	interpolateValue = (elem) => {
		let params = {
			day: elem.day, month: elem.month, year: elem.year,
			category: this.state.selectedCategory,
			stationCode: elem.stationCode
		};
		loadingSwal.fire('Intepolazione dati in corso...');
		DailyStatsClient.interpolateDailyStatsByDate(
			(data) => {
				loadingSwal.close();
				data.dirty = true;
				let newData = this.state.data;
				let currentDayData = newData.find(x => x.timestampKey === data.timestampKey).dailyStats;
				var index = _.findIndex(currentDayData, { stationCode: data.stationCode });
				currentDayData.splice(index, 1, data);
				this.addCalculatedFieldsToData(newData, this.state.stationMapByCode);
				this.setState({ data: newData, tableKey: (this.state.tableKey + 1) % 1000 });
			},
			(err) => { loadingSwal.close(); console.log("error"); },
			params
		);
	}

	calculateByDayAndStationCode = (elem) => {
		let params = {
			day: elem.day, month: elem.month, year: elem.year,
			category: this.state.selectedCategory,
			stationCode: elem.stationCode
		};
		loadingSwal.fire('Ricalcolo dati in corso...');
		DailyStatsClient.calculateDailyStatsByDate(
			(data) => {
				loadingSwal.close();
				if (data[0].dailyStats[0].validate !== "MISSING") {
					data[0].dailyStats[0].dirty = true;
				}
				let newData = this.state.data;
				let currentDayData = newData.find(x => x.timestampKey === data[0].timestampKey).dailyStats;
				var index = _.findIndex(currentDayData, { stationCode: data[0].dailyStats[0].stationCode });
				currentDayData.splice(index, 1, data[0].dailyStats[0]);
				this.addCalculatedFieldsToData(newData, this.state.stationMapByCode);
				this.setState({ data: newData, tableKey: (this.state.tableKey + 1) % 1000 });
			},
			(err) => { loadingSwal.close(); console.log("error"); },
			params
		);
	}

	buildTable() {
		if (!this.state.data) {
			return (
				<>
					<Skeleton height={"10vh"} duration={8} />
					<Skeleton height={"70vh"} duration={8} />
				</>
			);
		}

		let stationLabel = <>{MEASUREMENT_CATEGORY_ICONS[this.state.selectedCategory]} {MEASUREMENT_LABELS[this.state.selectedCategory]} {!!this.state.selectedStation.value && "  -  " + this.state.selectedStation.label}</>;
		let title = <h3>Dati giornalieri {stationLabel}</h3>;

		return (
			<>
				<div className="row">
					<div className=" col-4 ml-auto my-auto">
						{title}
					</div>
					<div className=" col-2 d-flex justify-content-center m-auto">
						{this.props.canEdit && <MassiveValidationForm
							id="selectValidation"
							label="Seleziona una validazione"
							disabled={!this.isOfficer()}
							selectedCategory={this.state.selectedCategory}
							selectedMassiveValidation={this.state.selectedMassiveValidation}
							handleMassiveValidation={(val) => this.massiveValidation(val)}
						/>}
					</div>
					<div className="col-2 d-flex justify-content-center m-auto">
						{this.props.canEdit && <Button
							startIcon={<FaCalculator />}
							size="small"
							disabled={!this.isOfficer()}
							variant="contained"
							color="primary"
							onClick={() => this.calculateDailyStats()}
						>Ricalcola</Button>}
					</div>
					<div className="col-2 d-flex justify-content-center m-auto">
						<Button startIcon={<FaFileCsv />} size="small" variant="contained" color="primary" onClick={() => this.downloadDataCsv()}>Download csv</Button>
					</div>
					<div className="col-2 d-flex justify-content-center m-auto">
						<Button startIcon={<FaFilePdf />} size="small" variant="contained" color="primary" onClick={() => this.downloadDataPdf()}>Download pdf</Button>
					</div>
				</div>

				<div style={{ width: "92vw" }}>
					<DailyStatsTables
						selectedCategory={this.state.selectedCategory}
						data={this.state.data}
						tableKey={this.state.tableKey}
						calculateByDay={elem => this.calculateByDay(elem)}
						calculateByDayAndStationCode={elem => this.calculateByDayAndStationCode(elem)}
						nested={this.state.allStations}
						updateTableKey={() => this.setState({ tableKey: (this.state.tableKey + 1) % 1000 })}
						interpolateValue={elem => this.interpolateValue(elem)}
						canEdit={this.props.canEdit && this.isOfficer()}
						tableRef={el => this.tableRef = el}
						downloadData={() => this.downloadDataCsv()}
						label={stationLabel}
					/>
				</div>
				<div className="d-flex">
					{this.props.canEdit && <Button
						size="small"
						className="justify-content-end ml-auto"
						variant="contained"
						color="primary"
						endIcon={<SendSharpIcon />}
						onClick={() => this.updateDailyStats()}
						disabled={this.getDirtyStats().length === 0}
					>Salva</Button>}
				</div>
			</>
		);
	}
}

export default DailyStatsPage;

const MassiveValidationForm = (props) => {
	let validationOptions = null;
	switch (props.selectedCategory) {
		case "P": validationOptions = rainValidationOptions; break;
		case "T": validationOptions = temperatureValidationOptions; break;
		case "I": validationOptions = outflowValidationOptions; break;
		default: break;
	}

	let options = Object.keys(validationOptions).map(x => <MenuItem key={x} value={x}>{validationOptions[x]}</MenuItem>);
	let classname = !!props.className ? props.className : "col-12 row justify-content-center";

	return (
		<FormControl className={classname} key={"select_validation_form"}>
			<InputLabel id="h-helper-label_2">{props.label}</InputLabel>
			<Select
				disabled={props.disabled}
				fullWidth
				labelId={props.label}
				id={props.id}
				value={props.selectedMassiveValidation}
				onChange={(e) => props.handleMassiveValidation(e.target.value)}
			>
				{options}
			</Select>
		</FormControl>
	);
};
